import { CompleteKundali, Language, PlanetName, ZodiacSign } from '../types/kundali';
import { ZODIAC_LORDS, getSavByBhava } from './vedicEngine';

export interface CareerAptitudeVerdict {
  jobScore: number;           // 0 to 100
  businessScore: number;      // 0 to 100
  primaryType: 'BUSINESS_ENTREPRENEUR' | 'CORPORATE_EXECUTIVE' | 'HYBRID_INTRAPRENEUR';
  verdictTitleEn: string;
  verdictTitleHi: string;
  summaryEn: string;
  summaryHi: string;
  h6VsH7Comparison: {
    h6Lord: PlanetName;
    h6Sign: ZodiacSign;
    h6SAV: number;
    h7Lord: PlanetName;
    h7Sign: ZodiacSign;
    h7SAV: number;
    favoredPath: 'JOB' | 'BUSINESS' | 'BALANCED';
    explanationEn: string;
    explanationHi: string;
  };
  parakramaRiskQuotient: {
    h3Lord: PlanetName;
    h3SAV: number;
    riskCategory: 'HIGH_APPETITE' | 'CALCULATED_MODERATE' | 'RISK_AVERSE';
    explanationEn: string;
    explanationHi: string;
  };
  wealthAccumulationAxis: {
    h2SAV: number;
    h11SAV: number;
    cashFlowStability: string;
  };
  strengthsJob: string[];
  strengthsBusiness: string[];
}

export interface DualTrackPhase {
  id: string;
  timeRange: string;         // e.g., "2026 - 2027"
  startDate: string;
  endDate: string;
  activeDasha: string;       // e.g., "Jupiter MD • Saturn AD"
  mahadashaLord: PlanetName;
  antardashaLord: PlanetName;
  
  // Job / Employment Track
  jobTrack: {
    score: number;           // 0 - 100
    statusLabelEn: string;   // e.g., "Prime Promotion Window"
    statusLabelHi: string;
    rating: 'PRIME' | 'FAVORABLE' | 'NEUTRAL_STABLE' | 'STRESS_RESTRUCTURE';
    keyWindowEn: string;     // e.g., "Q4 2026 - Q1 2027"
    keyWindowHi: string;
    adviceEn: string;
    adviceHi: string;
    favorableActions: string[];
  };

  // Business / Enterprise Track
  businessTrack: {
    score: number;           // 0 - 100
    statusLabelEn: string;   // e.g., "★★★★★ Prime Market Launch"
    statusLabelHi: string;
    rating: 'PRIME_LAUNCH' | 'EXPANSION' | 'MVP_RESEARCH_ONLY' | 'HIGH_RISK_POSTPONE';
    keyWindowEn: string;     // e.g., "Feb 2027 - Aug 2027"
    keyWindowHi: string;
    adviceEn: string;
    adviceHi: string;
    recommendedSectors: string[];
  };

  // Strategic Alignment
  strategicSynthesisEn: string;
  strategicSynthesisHi: string;
  planetaryClimate: string[];
}

export interface ExtendedCareerHorizonPhase {
  id: string;
  periodLabel: string;       // e.g. "2031 - 2035 (Saturn Mahadasha)"
  approximateYears: string;  // e.g. "Age 38 - 42"
  dominantMahadasha: PlanetName;
  
  jobTrajectory: {
    score: number;
    statureEn: string;       // e.g., "C-Suite Executive / Senior Director"
    statureHi: string;
    outlookEn: string;
    outlookHi: string;
  };

  businessTrajectory: {
    score: number;
    statureEn: string;       // e.g., "Multi-Venture Scaling / Board Chairman"
    statureHi: string;
    outlookEn: string;
    outlookHi: string;
  };

  macroThemes: string[];
}

export interface ComprehensiveCareerAnalysis {
  aptitude: CareerAptitudeVerdict;
  next5YearsPhases: DualTrackPhase[];
  beyond5YearsPhases: ExtendedCareerHorizonPhase[];
}

/**
 * Calculates complete career evaluation: Business vs Job, Next 5 Years, and Beyond 5 Years
 */
export function analyzeCareerTimeline(kundali: CompleteKundali, lang: Language = 'en'): ComprehensiveCareerAnalysis {
  const isHi = lang === 'hi';

  const houses = kundali.houses || [];
  const planets = kundali.planets || [];
  const sav = kundali.ashtakvarga?.sarvashtakvarga || [];

  const h1 = houses[0] || { sign: 'Aries', signLord: 'Mars' };
  const h2 = houses[1] || { sign: 'Taurus', signLord: 'Venus' };
  const h3 = houses[2] || { sign: 'Gemini', signLord: 'Mercury' };
  const h6 = houses[5] || { sign: 'Virgo', signLord: 'Mercury' };
  const h7 = houses[6] || { sign: 'Libra', signLord: 'Venus' };
  const h10 = houses[9] || { sign: 'Capricorn', signLord: 'Saturn' };
  const h11 = houses[10] || { sign: 'Aquarius', signLord: 'Saturn' };

  const savH2 = getSavByBhava(kundali.ashtakvarga, 2, kundali.houses);
  const savH3 = getSavByBhava(kundali.ashtakvarga, 3, kundali.houses);
  const savH6 = getSavByBhava(kundali.ashtakvarga, 6, kundali.houses);
  const savH7 = getSavByBhava(kundali.ashtakvarga, 7, kundali.houses);
  const savH10 = getSavByBhava(kundali.ashtakvarga, 10, kundali.houses);
  const savH11 = getSavByBhava(kundali.ashtakvarga, 11, kundali.houses);

  // 1. Classical Multi-Factor Aptitude Scoring: Job vs. Business
  // Baseline neutral starting point
  let jobPoints = 50;
  let businessPoints = 50;

  // Factor A: 6th House (Service / Seva / Corporate Hierarchy) vs 7th House (Commerce / Vyapar / Trade) SAV
  const diff67 = savH6 - savH7;
  jobPoints += diff67 * 2.8;
  businessPoints -= diff67 * 2.8;

  // Individual strength of 6th & 7th against baseline 28
  jobPoints += (savH6 - 28) * 1.8;
  businessPoints += (savH7 - 28) * 2.0;

  // Factor B: 6th House Lord & Occupants (Service Stature & Workplace Resilience)
  const h6LordPlanet = planets.find(p => p.name === h6.signLord);
  if (h6LordPlanet) {
    // If 6th lord is well-placed in Kendra/Trikona or 6th, native manages service duties effectively
    if ([1, 4, 6, 9, 10].includes(h6LordPlanet.house)) {
      jobPoints += 8;
    }
  }
  const planetsInH6 = planets.filter(p => p.house === 6);
  planetsInH6.forEach(p => {
    if (p.name === 'Saturn') jobPoints += 14; // Saturn in 6th: legendary stamina in service
    if (p.name === 'Sun') jobPoints += 11;    // Sun in 6th: Shatru-hanta, wins corporate promotions
    if (p.name === 'Mars') jobPoints += 10;   // Mars in 6th: high competitive drive
    if (p.name === 'Rahu') jobPoints += 8;    // Rahu in 6th: excels in complex organizational politics
  });

  // Factor C: 7th House Lord & Occupants (Public Commerce, Trade & Entrepreneurship)
  const h7LordPlanet = planets.find(p => p.name === h7.signLord);
  if (h7LordPlanet) {
    // If 7th lord is placed in Artha/Labha/Vyapar houses (2, 7, 10, 11, 3)
    if ([2, 7, 10, 11, 3].includes(h7LordPlanet.house)) {
      businessPoints += 12;
    }
    if (['Exalted', 'Own House'].includes(h7LordPlanet.dignity || '')) {
      businessPoints += 10;
    }
  }
  const planetsInH7 = planets.filter(p => p.house === 7);
  planetsInH7.forEach(p => {
    if (p.name === 'Mercury') businessPoints += 16; // Mercury in 7th: born commercial dealmaker
    if (p.name === 'Venus') businessPoints += 12;   // Venus in 7th: client acquisition, public contracts
    if (p.name === 'Rahu') businessPoints += 14;    // Rahu in 7th: disruptive, global trade
    if (p.name === 'Jupiter') businessPoints += 10; // Jupiter in 7th: respected firm/partnership
    if (p.name === 'Saturn') {
      if (['Exalted', 'Own House'].includes(p.dignity || '')) {
        businessPoints += 10; // Sasa Yoga / strong partner enterprise
      } else {
        jobPoints += 6;       // Saturn in 7th brings cautious, slow business expansion
      }
    }
  });

  // Factor D: 10th House (Karma Bhava) - Executive Gravity vs Independent Venture
  const h10LordPlanet = planets.find(p => p.name === h10.signLord);
  if (h10LordPlanet) {
    if (h10LordPlanet.house === 6) jobPoints += 12;         // 10th lord in 6th = career through salaried service
    if ([7, 11, 3].includes(h10LordPlanet.house)) businessPoints += 12; // 10th lord in 7/11/3 = career through commerce/venture
  }
  const planetsInH10 = planets.filter(p => p.house === 10);
  planetsInH10.forEach(p => {
    if (p.name === 'Sun') {
      jobPoints += 14;        // Digbala Sun in 10th = C-suite corporate executive or government authority
      businessPoints += 7;
    }
    if (p.name === 'Mars') {
      jobPoints += 12;        // Digbala Mars in 10th = powerful institutional authority & leadership
      businessPoints += 9;
    }
    if (p.name === 'Saturn') {
      jobPoints += 14;        // Saturn in 10th = steady corporate seniority & compounding tenure
      businessPoints += 5;
    }
    if (p.name === 'Mercury') {
      businessPoints += 12;   // Mercury in 10th = advisory, consulting, trading business
      jobPoints += 8;
    }
    if (p.name === 'Rahu') {
      businessPoints += 14;   // Rahu in 10th = startup founder, tech entrepreneur, rapid pivot
      jobPoints += 6;
    }
  });

  // Factor E: 3rd House (Parakrama / Risk Tolerance)
  // Enterprise requires high risk appetite; corporate jobs favor stability
  if (savH3 >= 29) {
    businessPoints += (savH3 - 28) * 2.6;
  } else if (savH3 <= 25) {
    const penalty = (26 - savH3) * 2.8;
    businessPoints -= penalty;
    jobPoints += penalty * 0.9;
  }

  // Factor F: 11th House (Profits/Labha) vs 2nd House (Dhana/Savings)
  // Scaling business depends on 11th house gains; salaried careers prioritize 2nd house savings stability
  businessPoints += (savH11 - 28) * 1.8;
  jobPoints += (savH2 - 28) * 1.5;

  // Factor G: Mercury (Karaka of Vyapar/Trade) & Saturn (Karaka of Seva/Service)
  const mercury = planets.find(p => p.name === 'Mercury');
  if (mercury) {
    if (['Exalted', 'Own House'].includes(mercury.dignity || '')) businessPoints += 14;
    else if (['Gemini', 'Virgo', 'Taurus', 'Libra', 'Aquarius'].includes(mercury.sign)) businessPoints += 8;
    if ([1, 4, 7, 10].includes(mercury.house)) businessPoints += 6;
  }

  const saturn = planets.find(p => p.name === 'Saturn');
  if (saturn) {
    if (['Exalted', 'Own House'].includes(saturn.dignity || '')) jobPoints += 12;
    else if (['Libra', 'Capricorn', 'Aquarius'].includes(saturn.sign)) jobPoints += 8;
    if ([6, 10, 11].includes(saturn.house)) jobPoints += 6;
  }

  // Factor H: Shadbala Virupas (if available)
  const shadbalaList = kundali.shadbala?.planets;
  if (shadbalaList) {
    const mercItem = shadbalaList.find(p => p.planet === 'Mercury');
    const satItem = shadbalaList.find(p => p.planet === 'Saturn');
    const mercVirupas = mercItem?.totalVirupas || 350;
    const satVirupas = satItem?.totalVirupas || 350;
    const shadbalaDiff = mercVirupas - satVirupas;
    if (shadbalaDiff > 30) businessPoints += 7;
    else if (shadbalaDiff < -30) jobPoints += 7;
  }

  // Normalize scores into realistic, distinct percentages (scale 26 to 92)
  let jobScore = Math.round(Math.max(26, Math.min(92, jobPoints)));
  let businessScore = Math.round(Math.max(26, Math.min(92, businessPoints)));

  // Ensure differentiated, honest presentation
  let primaryType: CareerAptitudeVerdict['primaryType'] = 'HYBRID_INTRAPRENEUR';
  let verdictTitleEn = 'Hybrid Intrapreneur / Corporate Founder';
  let verdictTitleHi = 'हाइब्रिड उद्यमी / कॉर्पोरेट नेतृत्व';
  let summaryEn = '';
  let summaryHi = '';

  const scoreDiff = businessScore - jobScore;

  if (scoreDiff >= 8) {
    primaryType = 'BUSINESS_ENTREPRENEUR';
    verdictTitleEn = 'Natural Entrepreneur & Independent Founder';
    verdictTitleHi = 'प्राकृतिक उद्यमी एवं स्वतंत्र व्यवसाय अनुकूलता';
    summaryEn = `Your planetary architecture exhibits strong 7th house trade vibrations and 3rd house risk quotient (${savH7} SAV in 7th, ${savH3} SAV in 3rd). You thrive when managing autonomous capital, negotiating public contracts, and building enterprise value rather than reporting to corporate superiors.`;
    summaryHi = `आपकी जन्मकुंडली में सप्तम भाव (व्यापार) और तृतीय भाव (पराक्रम) का प्रभाव सशक्त है (${savH7} बिंदु ७वें भाव में, ${savH3} बिंदु ३रे भाव में)। आप स्वतंत्र निर्णय, वाणिज्यिक अनुबंध और स्वयं का उद्यम स्थापित करने में सर्वाधिक सफल होंगे।`;
  } else if (scoreDiff <= -8) {
    primaryType = 'CORPORATE_EXECUTIVE';
    verdictTitleEn = 'Corporate Executive & Institutional Leader';
    verdictTitleHi = 'उच्च कॉर्पोरेट अधिकारी एवं संगठनात्मक नेतृत्व';
    summaryEn = `With powerful 6th house resilience (${savH6} SAV) and 10th house institutional gravity (${savH10} SAV), your chart favors executive hierarchies, multinational organizations, and structured corporate governance where methodical authority compounds over time.`;
    summaryHi = `षष्ठम भाव (सेवा व प्रतिस्पर्धा: ${savH6} बिंदु) और दशम भाव (अधिकार: ${savH10} बिंदु) की प्रबलता यह दर्शाती है कि आप बड़े संगठनों, बहुराष्ट्रीय कंपनियों या प्रशासनिक सेवा में निरंतर पदोन्नति पाकर उच्च प्रतिष्ठा प्राप्त करेंगे।`;
  } else {
    primaryType = 'HYBRID_INTRAPRENEUR';
    verdictTitleEn = 'Executive Intrapreneur / Dual-Track Specialist';
    verdictTitleHi = 'हाइब्रिड कार्यकारी / परामर्शदाता व समानांतर व्यवसाय';
    summaryEn = `Both service (6th house: ${savH6} SAV) and business (7th house: ${savH7} SAV) houses are equally balanced. You are ideally suited to hold executive leadership in corporate employment while operating consultancies, investments, or scalable side-ventures simultaneously.`;
    summaryHi = `आपकी कुंडली में नौकरी (६ठा भाव: ${savH6} बिंदु) और व्यवसाय (७वां भाव: ${savH7} बिंदु) दोनों के कारक संतुलित हैं। आप मुख्य नौकरी के साथ-साथ परामर्श या समानांतर उद्यम चलाकर दोहरा लाभ अर्जित कर सकते हैं।`;
  }

  const h6VsH7Favored = savH7 > savH6 ? 'BUSINESS' : savH6 > savH7 ? 'JOB' : 'BALANCED';

  const aptitude: CareerAptitudeVerdict = {
    jobScore,
    businessScore,
    primaryType,
    verdictTitleEn,
    verdictTitleHi,
    summaryEn,
    summaryHi,
    h6VsH7Comparison: {
      h6Lord: h6.signLord as PlanetName,
      h6Sign: h6.sign as ZodiacSign,
      h6SAV: savH6,
      h7Lord: h7.signLord as PlanetName,
      h7Sign: h7.sign as ZodiacSign,
      h7SAV: savH7,
      favoredPath: h6VsH7Favored,
      explanationEn: `6th house (daily service/rivalry) has ${savH6} SAV bindus ruled by ${h6.signLord}, while 7th house (public trade/commerce) has ${savH7} SAV bindus ruled by ${h7.signLord}. ${savH7 > savH6 ? 'The trade axis outweighs service.' : 'The service axis provides solid grounding.'}`,
      explanationHi: `षष्ठम भाव (${h6.signLord}) में ${savH6} बिंदु हैं जबकि सप्तम भाव (${h7.signLord}) में ${savH7} बिंदु हैं। ${savH7 > savH6 ? 'सप्तम भाव व्यापार को अधिक बल प्रदान कर रहा है।' : 'षष्ठम भाव सेवा व नियमित पदोन्नति के लिए अधिक दृढ़ है।'}`
    },
    parakramaRiskQuotient: {
      h3Lord: h3.signLord as PlanetName,
      h3SAV: savH3,
      riskCategory: savH3 >= 29 ? 'HIGH_APPETITE' : savH3 >= 26 ? 'CALCULATED_MODERATE' : 'RISK_AVERSE',
      explanationEn: `3rd house (Parakrama/Initiative) holds ${savH3} SAV bindus. ${savH3 >= 29 ? 'High courage to absorb startup turbulence and capital risks.' : savH3 >= 26 ? 'Prefers calculated risks with institutional backing.' : 'Strongly prefers risk-free fixed salaried security.'}`,
      explanationHi: `तृतीय भाव (पराक्रम) में ${savH3} बिंदु जोखिम उठाने की क्षमता को दर्शाते हैं। ${savH3 >= 29 ? 'उद्यम के उतार-चढ़ाव सहने की उत्तम क्षमता।' : 'सुरक्षित व नियमित वित्तीय आधार को प्राथमिकता।'}`
    },
    wealthAccumulationAxis: {
      h2SAV: savH2,
      h11SAV: savH11,
      cashFlowStability: `2nd house (savings): ${savH2} SAV • 11th house (revenue/profits): ${savH11} SAV`
    },
    strengthsJob: [
      `High administrative stamina in complex corporate hierarchies`,
      `Resilience against workplace competition and peer rivalry (6th house: ${savH6} SAV)`,
      `Compounding seniority and executive tenure with long-term compensation growth`
    ],
    strengthsBusiness: [
      `Innate trade negotiation and client acquisition potential (7th house: ${savH7} SAV)`,
      `Profits & networking multiplier (11th house: ${savH11} SAV)`,
      `Capability to pivot and adapt to open-market demands`
    ]
  };

  // 2. Next 5 Years Chronological Dual-Track Phases (Calculated dynamically per native's Dasha)
  const currentYear = new Date().getFullYear();
  const dashaList = kundali.dashas || [];
  const nowTs = new Date().getTime();

  let activeMD = dashaList.find(d => {
    const s = new Date(d.startDate).getTime();
    const e = new Date(d.endDate).getTime();
    return nowTs >= s && nowTs <= e;
  }) || dashaList[0] || { planet: 'Jupiter', startDate: '2022-01-01', endDate: '2038-01-01', antardashas: [] };

  const antardashas = activeMD.antardashas || [];

  // Helper function to calculate phase scores dynamically based on AD planet's chart role
  const calculatePhaseScores = (adPlanet: PlanetName) => {
    let jobPhaseMod = 0;
    let bizPhaseMod = 0;

    const adPlanetData = planets.find(p => p.name === adPlanet);
    const adHouse = adPlanetData?.house || 1;
    const adSAV = sav[adHouse - 1] ?? 28;

    // AD House Placement Effects
    if ([6, 10, 1].includes(adHouse)) jobPhaseMod += 10;
    if ([7, 11, 3, 2].includes(adHouse)) bizPhaseMod += 12;

    // AD Natural Karakatva
    if (['Saturn', 'Sun', 'Mars'].includes(adPlanet)) jobPhaseMod += 8;
    if (['Mercury', 'Venus', 'Rahu'].includes(adPlanet)) bizPhaseMod += 9;
    if (adPlanet === 'Jupiter') {
      jobPhaseMod += 6;
      bizPhaseMod += 6;
    }

    // SAV modifier of the AD house
    const savModifier = Math.round((adSAV - 28) * 1.2);
    jobPhaseMod += savModifier;
    bizPhaseMod += savModifier;

    // Blend foundational aptitude (50% weight) with phase astrological climate (50% weight)
    const dynamicJobScore = Math.max(38, Math.min(96, Math.round(aptitude.jobScore * 0.52 + 35 + jobPhaseMod)));
    const dynamicBizScore = Math.max(38, Math.min(96, Math.round(aptitude.businessScore * 0.52 + 33 + bizPhaseMod)));

    return { dynamicJobScore, dynamicBizScore, adHouse, adSAV };
  };

  // Generate 3 sequential phases covering next 5 years
  const next5YearsPhases: DualTrackPhase[] = [];

  // Phase 1: Near-term (Year 1 - Year 2: e.g. 2026 - 2027)
  const ad1 = antardashas.find(ad => {
    const e = new Date(ad.endDate).getTime();
    return e > nowTs;
  }) || { planet: 'Saturn', startDate: `${currentYear}-01-01`, endDate: `${currentYear + 1}-08-15` };

  const ad1Planet = ad1.planet;
  const p1Scores = calculatePhaseScores(ad1Planet);
  const isP1PrimeJob = p1Scores.dynamicJobScore >= 78;
  const isP1PrimeBiz = p1Scores.dynamicBizScore >= 78;

  next5YearsPhases.push({
    id: 'phase-1',
    timeRange: `${currentYear} – ${currentYear + 1}`,
    startDate: `${currentYear}-01-01`,
    endDate: `${currentYear + 1}-12-31`,
    activeDasha: `${activeMD.planet} MD • ${ad1Planet} AD`,
    mahadashaLord: activeMD.planet,
    antardashaLord: ad1Planet,
    jobTrack: {
      score: p1Scores.dynamicJobScore,
      statusLabelEn: isP1PrimeJob ? 'Prime Promotion & Authority Window' : 'Consolidation & Stable Tenure',
      statusLabelHi: isP1PrimeJob ? 'पदोन्नति एवं अधिकार वृद्धि काल' : 'स्थिर सेवा एवं अनुभव संचय',
      rating: isP1PrimeJob ? 'PRIME' : 'NEUTRAL_STABLE',
      keyWindowEn: `Q3 ${currentYear} – Q1 ${currentYear + 1}`,
      keyWindowHi: `तिमाही ३ ${currentYear} – तिमाही १ ${currentYear + 1}`,
      adviceEn: isP1PrimeJob 
        ? `Auspicious period for appraisal negotiations and expanding managerial scope. Maintain strong alignment with institutional sponsors.`
        : `Consolidate existing responsibilities, deepen functional competencies, and build organizational allies without taking uncalculated career risks.`,
      adviceHi: isP1PrimeJob 
        ? `वेतन वृद्धि एवं पदोन्नति की बातचीत के लिए उत्तम समय। वरिष्ठ अधिकारियों के साथ तालमेल बनाए रखें।`
        : `वर्तमान जिम्मेदारियों को सुदृढ़ करें और जोखिम भरे बदलावों से बचते हुए अनुभव संचित करें।`,
      favorableActions: ['Seek executive sponsorship', 'Take leadership on high-visibility projects', 'Consolidate organizational tenure']
    },
    businessTrack: {
      score: p1Scores.dynamicBizScore,
      statusLabelEn: isP1PrimeBiz ? '★ Active Venture Pilot & Commercial Window' : 'Stealth Product Ideation & Low-Capital Testing',
      statusLabelHi: isP1PrimeBiz ? '★ सक्रिय व्यापार शुभारंभ एवं वाणिज्यिक काल' : 'नवाचार विचार एवं कम पूंजी परीक्षण',
      rating: isP1PrimeBiz ? 'PRIME_LAUNCH' : 'MVP_RESEARCH_ONLY',
      keyWindowEn: `Q4 ${currentYear} – Q2 ${currentYear + 1}`,
      keyWindowHi: `तिमाही ४ ${currentYear} – तिमाही २ ${currentYear + 1}`,
      adviceEn: isP1PrimeBiz 
        ? `High planetary momentum for commercial testing. Deploy MVP, secure early client contracts, and establish corporate vendor networks.`
        : `Validate market demand with low fixed costs. Retain salaried stability while validating legal entity and vendor networks.`,
      adviceHi: isP1PrimeBiz 
        ? `व्यापारिक परीक्षण के लिए अनुकूल काल। उत्पाद लॉन्च करें और प्रारंभिक ग्राहकों से अनुबंध करें।`
        : `कम लागत पर बाजार मांग का परीक्षण करें। वर्तमान नौकरी की आय बनाए रखते हुए व्यावसायिक ढांचे का निर्माण करें।`,
      recommendedSectors: ['Strategic Advisory', 'Digital Commerce', 'B2B Services']
    },
    strategicSynthesisEn: p1Scores.dynamicBizScore > p1Scores.dynamicJobScore
      ? `Commercial advantage phase: Planetary transits favor independent initiative and enterprise incubation alongside salaried safety.`
      : `Institutional consolidation phase: Retain current job compensation while quietly architecting long-term systems. Do not make an abrupt resignation.`,
    strategicSynthesisHi: p1Scores.dynamicBizScore > p1Scores.dynamicJobScore
      ? `व्यावसायिक प्राथमिकता काल: वर्तमान आय के साथ-साथ स्वतंत्र उद्यम की नींव रखने के लिए ग्रह स्थिति अत्यंत अनुकूल है।`
      : `संस्थागत स्थायित्व काल: वर्तमान नौकरी बनाए रखते हुए दीर्घकालिक लक्ष्यों की तैयारी करें। अचानक त्यागपत्र न दें।`,
    planetaryClimate: [
      `${activeMD.planet} Mahadasha provides foundational trajectory`,
      `${ad1Planet} Antardasha in ${p1Scores.adHouse}th house (${p1Scores.adSAV} SAV) activates career dynamics`
    ]
  });

  // Phase 2: Mid-term (Year 2 - Year 4: e.g. 2027 - 2029)
  const ad2Index = antardashas.findIndex(ad => ad.planet === ad1Planet) + 1;
  const ad2 = (ad2Index >= 0 && antardashas[ad2Index]) ? antardashas[ad2Index] : { planet: 'Mercury', startDate: `${currentYear + 2}-01-01`, endDate: `${currentYear + 3}-10-01` };
  const ad2Planet = ad2.planet;
  const p2Scores = calculatePhaseScores(ad2Planet);
  const isP2PrimeJob = p2Scores.dynamicJobScore >= 80;
  const isP2PrimeBiz = p2Scores.dynamicBizScore >= 80;

  next5YearsPhases.push({
    id: 'phase-2',
    timeRange: `${currentYear + 2} – ${currentYear + 3}`,
    startDate: `${currentYear + 2}-01-01`,
    endDate: `${currentYear + 3}-12-31`,
    activeDasha: `${activeMD.planet} MD • ${ad2Planet} AD`,
    mahadashaLord: activeMD.planet,
    antardashaLord: ad2Planet,
    jobTrack: {
      score: p2Scores.dynamicJobScore,
      statusLabelEn: isP2PrimeJob ? 'Strategic Company Switch / Title Elevation' : 'Operational Delivery & Executive Stature',
      statusLabelHi: isP2PrimeJob ? 'कंपनी परिवर्तन / उच्च पद पर नियुक्ति' : 'कार्यकारी दायित्व एवं पदोन्नति',
      rating: isP2PrimeJob ? 'PRIME' : 'FAVORABLE',
      keyWindowEn: `Mid ${currentYear + 2} – Early ${currentYear + 3}`,
      keyWindowHi: `मध्य ${currentYear + 2} – प्रारंभ ${currentYear + 3}`,
      adviceEn: isP2PrimeJob
        ? `Golden opportunity for lateral or external jump with substantial equity/compensation enhancement.`
        : `Strengthen core domain leadership and negotiate expanded authority in institutional hierarchies.`,
      adviceHi: isP2PrimeJob
        ? `नई कंपनी में बड़े पद और उच्च पैकेज के साथ जाने का श्रेष्ठतम अवसर।`
        : `संगठन में अपने नेतृत्व को मजबूत करें और महत्वपूर्ण परियोजनाओं की कमान संभालें।`,
      favorableActions: ['Target external executive offers', 'Negotiate profit-sharing / equity', 'Lead cross-border or strategic units']
    },
    businessTrack: {
      score: p2Scores.dynamicBizScore,
      statusLabelEn: isP2PrimeBiz ? '★★★★★ PRIME VENTURE LAUNCH & SCALE WINDOW' : 'Commercial Growth & Strategic Partnerships',
      statusLabelHi: isP2PrimeBiz ? '★★★★★ व्यापार शुभारंभ एवं विस्तार का सर्वश्रेष्ठ काल' : 'व्यापार विस्तार एवं रणनीतिक साझेदारी',
      rating: isP2PrimeBiz ? 'PRIME_LAUNCH' : 'EXPANSION',
      keyWindowEn: `Spring ${currentYear + 2} – Autumn ${currentYear + 3}`,
      keyWindowHi: `वसंत ${currentYear + 2} – शरद ${currentYear + 3}`,
      adviceEn: isP2PrimeBiz
        ? `Astrological green light! 7th Lord of Commerce and 11th Lord of Gains resonate strongly. Excellent timing for official incorporation, client acquisition, and capital deployment.`
        : `Favorable window for strategic partnerships and launching secondary revenue streams without quitting core work.`,
      adviceHi: isP2PrimeBiz
        ? `शुभ मुहूर्त काल! सप्तमेश एवं एकादशेश की अनुकूलता नए व्यापार के शुभारंभ, साझेदारी और ग्राहकों के अनुबंध के लिए अत्यंत फलदायी है।`
        : `नई व्यावसायिक साझेदारी और समानांतर आय के स्रोत शुरू करने के लिए उत्तम समय।`,
      recommendedSectors: ['Tech Platforms', 'Consultancy Trade', 'Specialized Contracting', 'Supply Chain']
    },
    strategicSynthesisEn: p2Scores.dynamicBizScore >= p2Scores.dynamicJobScore
      ? `⚡ Major Career Pivot Crossroads: Ideal climate to make the definitive leap into independent full-time business or equity partnerships.`
      : `⚡ Executive Stature Window: Accelerate institutional hierarchy climbing with elevated executive title and profit-sharing.`,
    strategicSynthesisHi: p2Scores.dynamicBizScore >= p2Scores.dynamicJobScore
      ? `⚡ कैरियर का मुख्य मोड़: स्वतंत्र पूर्णकालिक व्यवसाय या व्यावसायिक साझेदारी में पदार्पण करने का श्रेष्ठ समय।`
      : `⚡ उच्च पदोन्नति काल: कंपनी में शीर्ष नेतृत्व और हिस्सेदारी प्राप्त करने के लिए अनुकूल परिस्थितियां।`,
    planetaryClimate: [
      `${ad2Planet} in ${p2Scores.adHouse}th house triggers commercial and professional mobility`,
      `Transiting Jupiter activates foundational wealth axis`
    ]
  });

  // Phase 3: Consolidation (Year 4 - Year 5: e.g. 2029 - 2031)
  const ad3Index = ad2Index + 1;
  const ad3 = (ad3Index >= 0 && antardashas[ad3Index]) ? antardashas[ad3Index] : { planet: 'Venus', startDate: `${currentYear + 4}-01-01`, endDate: `${currentYear + 5}-12-31` };
  const ad3Planet = ad3.planet;
  const p3Scores = calculatePhaseScores(ad3Planet);
  const isP3PrimeJob = p3Scores.dynamicJobScore >= 76;
  const isP3PrimeBiz = p3Scores.dynamicBizScore >= 76;

  next5YearsPhases.push({
    id: 'phase-3',
    timeRange: `${currentYear + 4} – ${currentYear + 5}`,
    startDate: `${currentYear + 4}-01-01`,
    endDate: `${currentYear + 5}-12-31`,
    activeDasha: `${activeMD.planet} MD • ${ad3Planet} AD`,
    mahadashaLord: activeMD.planet,
    antardashaLord: ad3Planet,
    jobTrack: {
      score: p3Scores.dynamicJobScore,
      statusLabelEn: isP3PrimeJob ? 'Executive Autonomy & Strategic Governance' : 'Senior Advisory & Mentorship Role',
      statusLabelHi: isP3PrimeJob ? 'प्रशासनिक स्वायत्तता एवं रणनीतिक भूमिका' : 'वरिष्ठ सलाहकार एवं मार्गदर्शक भूमिका',
      rating: 'NEUTRAL_STABLE',
      keyWindowEn: `Throughout ${currentYear + 4}`,
      keyWindowHi: `वर्ष ${currentYear + 4} के दौरान`,
      adviceEn: `Shift focus toward managing teams and organizational architecture rather than individual operational execution.`,
      adviceHi: `दैनिक कार्यों के स्थान पर संगठन की रणनीति और नेतृत्व पर ध्यान केंद्रित करें।`,
      favorableActions: ['Board advisory positions', 'Departmental autonomy', 'Mentoring second-line management']
    },
    businessTrack: {
      score: p3Scores.dynamicBizScore,
      statusLabelEn: isP3PrimeBiz ? 'Multi-Stream Revenue & Institutional Scaling' : 'Consolidated Enterprise & Market Presence',
      statusLabelHi: isP3PrimeBiz ? 'बहु-आयामी आय एवं व्यावसायिक विस्तार' : 'स्थिर उद्यम एवं ब्रांड प्रतिष्ठा',
      rating: 'EXPANSION',
      keyWindowEn: `Mid ${currentYear + 4} – Late ${currentYear + 5}`,
      keyWindowHi: `मध्य ${currentYear + 4} – उत्तरार्ध ${currentYear + 5}`,
      adviceEn: `High liquidity and compounding revenue. Ideal for geographic expansion, recruiting senior partners, and institutionalizing brand presence.`,
      adviceHi: `व्यापार से निरंतर लाभ और विस्तार का समय। नए साझेदारों को जोड़ने और ब्रांड को स्थापित करने का उत्तम काल।`,
      recommendedSectors: ['Franchising & Licensing', 'Asset-backed Ventures', 'Strategic Joint Ventures']
    },
    strategicSynthesisEn: `Harvesting and compounding phase: Maximum cash-flow generation across either track with mature operational systems.`,
    strategicSynthesisHi: `लाभ संचय काल: दोनों में से चुने गए मार्ग पर स्थिर लाभ और दीर्घकालिक सुरक्षा प्राप्त होगी।`,
    planetaryClimate: [
      `${ad3Planet} in ${p3Scores.adHouse}th house blesses revenue flow and luxury asset acquisition`,
      `Saturn stabilizes corporate and entrepreneurial foundations`
    ]
  });

  // 3. Beyond 5 Years Extended Horizon Phases (Dynamic Trajectory calculated from Natal Blueprint)
  const beyondJob1 = Math.max(45, Math.min(94, Math.round(aptitude.jobScore * 0.6 + (savH10 >= 30 ? 38 : 30))));
  const beyondBiz1 = Math.max(45, Math.min(94, Math.round(aptitude.businessScore * 0.6 + (savH11 >= 30 ? 38 : 30))));

  const beyondJob2 = Math.max(40, Math.min(92, Math.round(aptitude.jobScore * 0.55 + (savH6 >= 28 ? 36 : 28))));
  const beyondBiz2 = Math.max(40, Math.min(96, Math.round(aptitude.businessScore * 0.62 + (savH7 >= 28 ? 38 : 30))));

  const beyond5YearsPhases: ExtendedCareerHorizonPhase[] = [
    {
      id: 'beyond-1',
      periodLabel: `${currentYear + 6} – ${currentYear + 9} • Long-Term Apex Phase`,
      approximateYears: `Years 6 to 9 Ahead`,
      dominantMahadasha: activeMD.planet === 'Jupiter' ? 'Saturn' : activeMD.planet === 'Saturn' ? 'Mercury' : 'Jupiter',
      jobTrajectory: {
        score: beyondJob1,
        statureEn: 'Managing Director / Corporate Vice President / Board Member',
        statureHi: 'प्रबंध निदेशक / कॉरपोरेट उपाध्यक्ष / बोर्ड सदस्य',
        outlookEn: `Highest echelon of institutional power. You dictate policy, supervise large capital budgets, and command industry-wide reputation.`,
        outlookHi: `संस्थागत सत्ता का शिखर। आप नीतियों का निर्धारण करेंगे और व्यापक बजट व प्रतिष्ठा का संचालन करेंगे।`
      },
      businessTrajectory: {
        score: beyondBiz1,
        statureEn: 'Established Industry Enterprise / Multi-Brand Portfolio',
        statureHi: 'स्थापित उद्योगपति / बहु-ब्रांड पोर्टफोलियो',
        outlookEn: `Your enterprise achieves market leadership. You transition from founder-operator into Chairman/Owner with automated leadership teams.`,
        outlookHi: `आपका व्यवसाय बाजार में स्थापित होगा। आप दैनिक संचालन से ऊपर उठकर मुख्य रणनीतिकार और स्वामी की भूमिका में रहेंगे।`
      },
      macroThemes: [
        'Massive wealth compounding via Dhana Yogas in 2nd and 11th houses',
        'Industry recognition, awards, and national/global footprint',
        'Transition into mentorship and capital allocation'
      ]
    },
    {
      id: 'beyond-2',
      periodLabel: `${currentYear + 10} – ${currentYear + 15} • Legacy & Sovereign Authority`,
      approximateYears: `Years 10 to 15 Ahead`,
      dominantMahadasha: 'Mercury',
      jobTrajectory: {
        score: beyondJob2,
        statureEn: 'Senior Executive Advisor / Public Policy Consultant / Independent Director',
        statureHi: 'वरिष्ठ सलाहकार / स्वतंत्र निदेशक',
        outlookEn: `Unassailable professional standing with total flexibility, board compensation, and advisory prestige.`,
        outlookHi: `सर्वोच्च प्रतिष्ठा, परामर्श से आय और कार्य-समय की पूर्ण स्वतंत्रता।`
      },
      businessTrajectory: {
        score: beyondBiz2,
        statureEn: 'Generational Wealth Creation / Strategic Equity Exit',
        statureHi: 'पीढ़ीगत संपत्ति निर्माण / आंशिक इक्विटी बिक्री',
        outlookEn: `Prime timing for lucrative equity liquidation, family wealth office creation, and philanthropic foundations.`,
        outlookHi: `व्यावसायिक इक्विटी से विशाल पूंजी प्राप्ति, पारिवारिक संपत्ति ट्रस्ट और परोपकारी कार्यों का निर्माण।`
      },
      macroThemes: [
        'Creation of lasting legacy and family financial security',
        'Freedom from 9-to-5 daily operational grind',
        'Philanthropic leadership and community institutions'
      ]
    }
  ];

  return {
    aptitude,
    next5YearsPhases,
    beyond5YearsPhases
  };
}
